import type { ContentBlock, Report, ReportFile, ReportSection } from './types';

// Identity, versioning and image-URL rules ported from mobile
// (utils/reportIdentity.ts, utils/reportHelpers.ts, utils/reportViewUtils.ts).
// Keep them in sync — both clients write the same report lineage.

const CLOUDFRONT_BASE = 'https://d22xeeaijfewl2.cloudfront.net/public';
const VERSION_SUFFIX_RE = /_\d+\.\d+\.\d+\.json$/;

// ── Identity & versions ─────────────────────────────────────

/** 1 if a > b, -1 if a < b, 0 if equal (semver-ish, missing parts = 0). */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) {
      return diff > 0 ? 1 : -1;
    }
  }
  return 0;
}

/** Same rollover as mobile: 1.0.9 → 1.1.0, 1.9.9 → 2.0.0. */
export function incrementVersion(version: string): string {
  const parts = (version || '1.0.0').split('.').map(Number);
  if (parts[2] === 9 && parts[1] === 9) {
    return `${parts[0] + 1}.0.0`;
  }
  if (parts[2] === 9) {
    return `${parts[0]}.${parts[1] + 1}.0`;
  }
  return `${parts[0]}.${parts[1]}.${parts[2] + 1}`;
}

export function versionFromFile(file: Pick<ReportFile, 'id' | 'tags'>): string {
  const tag = file.tags?.find((t) => t.startsWith('v'));
  return tag
    ? tag.replace(/^v/, '')
    : file.id?.match(/_(\d+\.\d+\.\d+)\.json$/)?.[1] || '0.0.0';
}

export const fileNameForVersion = (baseId: string, version: string) =>
  `${baseId}_${version}.json`;

/**
 * Base report id (stable across versions; keys the byReportID GSI and the AI
 * edit thread). The file name is authoritative — never rebuild it from
 * createdAt for a loaded report (document-engine ids use epoch ms, GG-3537).
 */
export function resolveReportBaseId(report: {
  id?: string;
  reportID?: string | null;
  dealId?: string;
  createdAt?: string;
}): string | null {
  if (report.id) {
    return report.id.replace(VERSION_SUFFIX_RE, '');
  }
  if (report.reportID) {
    return report.reportID;
  }
  if (report.dealId && report.createdAt) {
    return `${report.dealId}.${report.createdAt.replace(/[:.]/g, '-')}`;
  }
  return null;
}

// ── Image URLs ──────────────────────────────────────────────

/** Reduce any stored image reference to a bare public S3 key ('' if none). */
export function extractStorageKey(value?: string | null): string {
  const raw = (value || '').trim();
  if (!raw || raw.startsWith('data:')) {
    return '';
  }
  let key = raw;
  if (raw.startsWith('http')) {
    if (raw.startsWith(CLOUDFRONT_BASE)) {
      key = raw.slice(CLOUDFRONT_BASE.length + 1);
    } else {
      const aws = raw.match(
        /^https:\/\/[^/]*\.amazonaws\.com\/(?:public\/)?([^?]+)/i
      );
      if (!aws) {
        return '';
      }
      key = aws[1];
    }
    key = key.split('?')[0];
  }
  return key
    .replace(/^s3:\/\//, '')
    .replace(/^\/+/, '')
    .replace(/^public\//, '');
}

/** Encode a path segment once, whether or not it arrived already encoded. */
function encodeSegment(segment: string): string {
  try {
    return encodeURIComponent(decodeURIComponent(segment));
  } catch {
    return encodeURIComponent(segment);
  }
}

/** Displayable URL for a stored image reference (key, s3://, CloudFront or AWS URL). */
export function toImageUrl(value?: string | null): string {
  const raw = (value || '').trim();
  if (!raw || raw.startsWith('data:')) {
    return raw;
  }
  const key = extractStorageKey(raw);
  if (key) {
    return `${CLOUDFRONT_BASE}/${key.split('/').map(encodeSegment).join('/')}`;
  }
  return raw.startsWith('http') ? raw : '';
}

// ── Save preparation ────────────────────────────────────────

/** Recursively drop null/undefined and editor-only `_`-prefixed keys. */
function clean(value: any): any {
  if (Array.isArray(value)) {
    return value.map(clean).filter((v) => v !== undefined);
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value).forEach(([k, v]) => {
      if (k.startsWith('_')) {
        return;
      }
      const c = clean(v);
      if (c !== undefined && c !== null) {
        out[k] = c;
      }
    });
    return out;
  }
  return value === null ? undefined : value;
}

/** Empty text blocks/list items are dropped on save, matching mobile's htmlToBlocks. */
function keepBlock(block: ContentBlock): ContentBlock | null {
  switch (block.type) {
    case 'heading':
    case 'paragraph':
      return block.text.trim() ? block : null;
    case 'bullet_list':
    case 'numbered_list': {
      const items = block.items.filter((i) => i.text.trim());
      return items.length ? { ...block, items } : null;
    }
    case 'image_gallery':
      return block.images.length ? block : null;
    default:
      return block;
  }
}

/**
 * Final report JSON for upload. Sections keep every field they arrived with
 * (spread, not whitelisted — the whitelist approach on mobile has dropped
 * engine anchors before, GG-3573); only their blocks and order change.
 */
export function prepareReportForSave(report: Report): Report {
  const sections: ReportSection[] = report.sections
    .map((section, index) => ({
      ...section,
      order: index,
      blocks: section.blocks
        .map(keepBlock)
        .filter((b): b is ContentBlock => b !== null),
    }))
    // A brand-new section left empty has nothing to keep; generated sections
    // keep their identity even when emptied so regeneration can track them.
    .filter(
      (s) => s.blocks.length > 0 || s.sectionId || s.taskId || s.findingId
    );
  return clean({ ...report, sections });
}

// ── Deal-derived defaults (cover "Fill empty fields") ──────

export function buildOrgAddress(org: any): string {
  if (!org) {
    return '';
  }
  return [
    org.contactAddress,
    org.contactAddress2,
    org.contactCity,
    org.contactState,
    org.contactZip,
  ]
    .filter(Boolean)
    .join(', ');
}

/** Port of mobile extractPropertyFromDeal (flexAttributes, then pinAddress). */
export function extractPropertyFromDeal(deal: any): Report['property'] {
  let address = '';
  let city = '';
  let name = deal?.name || '';
  let age = '';

  try {
    const flex =
      typeof deal?.flexAttributes === 'string'
        ? JSON.parse(deal.flexAttributes)
        : deal?.flexAttributes || {};
    address = flex.Address || flex.address || flex['Street Address'] || '';
    city = flex.City || flex.city || '';
    const contact = flex.contactInformation;
    if (contact && typeof contact === 'object') {
      address = address || contact.address || '';
      if (!city && contact.city) {
        city = [contact.city, contact.state, contact.postalCode]
          .filter(Boolean)
          .join(', ');
      }
      if (!name) {
        name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
      }
    }
  } catch {
    // unparseable flexAttributes — fall through to pinAddress
  }

  const pin = deal?.pinAddress;
  if (pin) {
    address = address || pin.address || '';
    if (!city && pin.city) {
      city = [pin.city, pin.region, pin.postalCode].filter(Boolean).join(', ');
    }
    name = name || pin.name || '';
    if (pin.homeBuiltYear) {
      age = String(pin.homeBuiltYear);
    }
  }

  return { name, age, address: address || deal?.description || '', city };
}

export function formatReportDate(value?: string): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
}

/** Real text: not blank and not a placeholder some records hold ("None"). */
export function isReal(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    !!value.trim() &&
    !['none', 'null', 'undefined', 'n/a'].includes(value.trim().toLowerCase())
  );
}

/** A profile field, or undefined when it holds a placeholder like "None". */
export function profileValue(value?: string | null): string | undefined {
  return isReal(value) ? value : undefined;
}

export interface CoverDefaults {
  title?: string;
  author?: Record<string, unknown>;
  company?: Record<string, unknown>;
  property?: Record<string, unknown>;
}

/**
 * Fill blank cover fields from `defaults` (rep profile, org, deal); fields
 * with real text are never touched. Returns the same report when nothing was
 * blank, plus how many fields were filled.
 */
export function fillCoverDefaults(
  report: Report,
  defaults: CoverDefaults
): { report: Report; filled: number } {
  const next: Record<string, any> = { ...report };
  let filled = 0;
  if (isReal(defaults.title) && !isReal(report.title)) {
    next.title = defaults.title;
    filled++;
  }
  (['author', 'company', 'property'] as const).forEach((group) => {
    const current: Record<string, unknown> = { ...(report[group] || {}) };
    let changed = false;
    Object.entries(defaults[group] || {}).forEach(([k, v]) => {
      if (isReal(v) && !isReal(current[k])) {
        current[k] = v;
        changed = true;
        filled++;
      }
    });
    if (changed) {
      next[group] = current;
    }
  });
  return { report: filled ? (next as Report) : report, filled };
}

/** The customer text mobile sends with a report link (smsShareUtils.ts). */
export function composeShareText(p: {
  homeownerName?: string;
  repName?: string;
  companyName?: string;
  link: string;
}): string {
  const who = p.companyName
    ? `${p.repName || ''} from ${p.companyName}`
    : p.repName || '';
  return (
    `Hi ${
      p.homeownerName || ''
    }, this is ${who}. I've completed your property inspection and put together a detailed report for you to review.\n\n` +
    `Tap the link below to view your inspection report — it includes photos, findings, and next steps:\n\n${p.link}\n\n` +
    "Let me know if you have any questions after reviewing it. I'm happy to walk you through everything."
  );
}

/**
 * Customer phone/email from a deal's flex attributes, found through the
 * category's field types (port of mobile utils/contactExtraction.ts):
 * direct "Phone"/"Email" fields first, then nested "Contact" fields.
 */
export function extractDealContacts(
  flexFields: Array<{ name: string; type: string }>,
  flex: Record<string, any>
): { phone: string; email: string } {
  const text = (v: unknown) => (typeof v === 'string' ? v : '');
  let phone = text(
    flex[flexFields.find((f) => f.type === 'Phone')?.name || '']
  );
  let email = text(
    flex[flexFields.find((f) => f.type === 'Email')?.name || '']
  );
  flexFields
    .filter((f) => f.type === 'Contact')
    .forEach((f) => {
      const contact = flex[f.name];
      if (contact && typeof contact === 'object') {
        phone = phone || text(contact.phoneNumber);
        email = email || text(contact.email);
      }
    });
  return { phone, email };
}
